import React, { useState, useEffect, useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { logout } from "../features/user/userSlice";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { base_url } from "../utils/axiosConfig";
import { CurrencyContext } from "../features/context/CurrencyContext";

const Cart = () => {
  const dispatch = useDispatch();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState({ type: null, value: 0, id: null });
  const [couponError, setCouponError] = useState("");
  const [couponDetails, setCouponDetails] = useState(null);
  const [referralCode, setReferralCode] = useState("");
  const [referralError, setReferralError] = useState("");
  const [referralVerified, setReferralVerified] = useState(false);

  // Loading states for individual actions
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [isVerifyingReferral, setIsVerifyingReferral] = useState(false);
  const [isUpdatingCart, setIsUpdatingCart] = useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const { convertPrice, getCurrencySymbol } = useContext(CurrencyContext);

  const token = useMemo(() => {
    const user = localStorage.getItem("user_website");
    return user ? JSON.parse(user) : null;
  }, []);

  const isWalletUser = useMemo(() => {
    return token?.data?.wallet?.referralWallet || false;
  }, [token]);

  const fetchCart = async () => {
    if (!token) {
      toast.error("Please log in to view your cart.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const getCartUrl = isWalletUser
        ? `${base_url}getProductInCart/member`
        : `${base_url}getProductInCart`;
      const response = await fetch(getCartUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token.token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          dispatch(logout());
          toast.error("Session expired. Please log in to view your cart.");
          return;
        }
        throw new Error(`Cart fetch error! status: ${response.status}`);
      }

      const cartData = await response.json();
      if (!cartData.cart || !Array.isArray(cartData.cart)) {
        setCartItems([]);
        return;
      }

      setCartItems(cartData?.cart);
    } catch (error) {
      console.error("Error fetching cart:", error);
      toast.error(`Failed to load cart: ${error.message}`);
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCart();
  }, [token, isWalletUser, dispatch]);

  const handleApplyCoupon = async () => {
    if (!couponCode) {
      setCouponError("Please enter a coupon code.");
      return;
    }

    setIsApplyingCoupon(true);
    try {
      const response = await fetch(
        `${base_url}coupon/getCoupon/${couponCode}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token.token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || "Invalid coupon code.");
      }

      const coupon = result.data;
      const currentDate = new Date();
      const startDate = new Date(coupon.startDate);
      const expirationDate = new Date(coupon.expirationDate);

      if (coupon.status !== "active") {
        throw new Error("Coupon is not active.");
      }
      if (currentDate < startDate || currentDate > expirationDate) {
        throw new Error("Coupon is expired or not yet valid.");
      }

      setDiscount({
        type: coupon.discountType,
        value: coupon.discountValue,
        id: coupon._id,
      });
      setCouponDetails({
        title: coupon.title,
        description: coupon.description,
        couponCode: coupon.couponCode,
        discountType: coupon.discountType,
        discountValue: coupon.discountValue,
      });
      setCouponError("");
      toast.success("Coupon applied successfully!");
    } catch (error) {
      setCouponError(error.message);
      setDiscount({ type: null, value: 0, id: null });
      setCouponDetails(null);
      toast.error(error.message);
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode("");
    setDiscount({ type: null, value: 0, id: null });
    setCouponError("");
    setCouponDetails(null);
    toast.success("Coupon removed successfully!");
  };

  const handleVerifyReferral = async () => {
    if (!referralCode) {
      setReferralError("Please enter a referral code.");
      return;
    }

    setIsVerifyingReferral(true);
    try {
      const response = await fetch(`${base_url}product/verifyReferralCode`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ referralCode }),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || "Invalid referral code.");
      }

      setReferralVerified(true);
      setReferralError("");
      toast.success("Referral code verified successfully!");
    } catch (error) {
      setReferralError(error.message);
      setReferralVerified(false);
      toast.error(error.message);
    } finally {
      setIsVerifyingReferral(false);
    }
  };

  const handleRemoveReferral = () => {
    setReferralCode("");
    setReferralVerified(false);
    setReferralError("");
    toast.success("Referral code removed successfully!");
  };

  const handleQuantityChange = async (item, delta) => {
    const newQuantity = item.quantity + delta;
    if (newQuantity < 1) return;

    setIsUpdatingCart(true);
    try {
      let response;
      const requestBody = {
        productId: item?.productId,
        categoryId: item?.categoryId,
        subCategoryId: item?.subCategoryId,
        quantity: 1,
      };
      if (item.subSubCategoryId)
        requestBody.subSubCategoryId = item.subSubCategoryId;
      if (item.subSubSubCategoryId)
        requestBody.subSubSubCategoryId = item.subSubSubCategoryId;
      if (item.variationId) requestBody.variationId = item.variationId;

      const cartUrl = isWalletUser
        ? delta > 0
          ? `${base_url}product/addtocartMember`
          : `${base_url}product/removeFromcartMember`
        : delta > 0
          ? `${base_url}product/addtoCart`
          : `${base_url}product/removeFromcart`;

      response = await fetch(cartUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          dispatch(logout());
          toast.error(
            responseData?.message || "Session expired. Please log in.",
          );
          throw new Error("Unauthorized");
        }
        throw new Error(
          responseData?.message || `HTTP error! status: ${response.status}`,
        );
      }

      await fetchCart();
      toast.success(
        responseData?.message ||
          `Quantity ${delta > 0 ? "increased" : "decreased"}`,
      );
    } catch (error) {
      console.error("Error updating quantity:", error);
      toast.error(
        error.message || "Failed to update quantity. Please try again.",
      );
    } finally {
      setIsUpdatingCart(false);
    }
  };

  const handleDelete = async (item) => {
    setIsUpdatingCart(true);
    try {
      const requestBody = {
        productId: item?.productId,
        categoryId: item.categoryId,
        subCategoryId: item.subCategoryId,
        quantity: item.quantity,
      };
      if (item.subSubCategoryId)
        requestBody.subSubCategoryId = item.subSubCategoryId;
      if (item.subSubSubCategoryId)
        requestBody.subSubSubCategoryId = item.subSubSubCategoryId;

      const removeUrl = isWalletUser
        ? `${base_url}product/removeFromcartMember`
        : `${base_url}product/removeFromcart`;

      const response = await fetch(removeUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        if (response.status === 401) {
          dispatch(logout());
          toast.error("Session expired. Please log in.");
          throw new Error("Unauthorized");
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await fetchCart();
      toast.success("Item removed from cart");
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error(`Failed to remove item: ${error.message}`);
    } finally {
      setIsUpdatingCart(false);
    }
  };

  const handleMakeOrder = async () => {
    setIsPlacingOrder(true);
    try {
      const formData = new FormData();
      formData.append(
        "totalPrice",
        localStorage.getItem("currency") === "INR"
          ? totalPrice.toFixed(2)
          : convertPrice(totalPrice.toFixed(2)),
      );
      formData.append(
        "totalPriceWithTax",
        localStorage.getItem("currency") === "INR"
          ? totalPriceWithTax.toFixed(2)
          : convertPrice(totalPriceWithTax.toFixed(2)),
      );
      if (hasPhysicalProducts) {
        formData.append(
          "deliveryAmount",
          localStorage.getItem("currency") === "INR"
            ? deliveryAmount.toFixed(2)
            : convertPrice(deliveryAmount.toFixed(2)),
        );
      }
      if (discount.id) {
        formData.append("couponId", discount.id);
        formData.append(
          "discountAmount",
          localStorage.getItem("currency") === "INR"
            ? discountAmount.toFixed(2)
            : convertPrice(discountAmount.toFixed(2)),
        );
      }
      if (referralVerified) {
        formData.append("referralCode", referralCode);
      }
      formData.append("currency", localStorage.getItem("currency"));

      const products = cartItems.map((item) => ({
        productTypeId: item.categoryId,
        subCategoryId: item.subCategoryId,
        subSubCategoryId: item.subSubCategoryId || undefined,
        subSubSubCategoryId: item.subSubSubCategoryId || undefined,
        productId: item.productId,
        variationId: item.variationId || undefined,
        quantity: item.quantity,
        pricePerProduct: item.price || 0,
      }));
      formData.append("products", JSON.stringify(products));

      const orderUrl = isWalletUser
        ? `${base_url}referral/buyProduct`
        : `${base_url}product/makeOrderByEndUser`;
      const response = await fetch(orderUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token.token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 401) {
          dispatch(logout());
          toast.error("Session expired. Please log in.");
          throw new Error("Unauthorized");
        }
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            `Order creation error! status: ${response.status}`,
        );
      }

      const result = await response.json();

      const cashfree = new window.Cashfree();
      cashfree
        .checkout({
          paymentSessionId: result.paymentSessionId,
          redirectTarget: "_self",
          mode: "production",
        })
        .then((result) => {
          console.log("Checkout result:", result);
        })
        .catch((err) => {
          console.error("Checkout error:", err);
        });
      toast.success(result.message || "Order placed successfully");
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error(error.message || "Failed to place order. Please try again.");
    } finally {
      setIsPlacingOrder(false);
    }
  };

  // Price calculations
  const hasPhysicalProducts = cartItems.some(
    (item) => item?.productDetails?.productType === "physical",
  );

  const deliveryAmount = hasPhysicalProducts ? 60 : 0;
  const subtotal = cartItems.reduce(
    (sum, item) => sum + (item?.price || 0) * (item?.quantity || 0),
    0,
  );
  let discountAmount = 0;
  if (discount.type === "percentage") {
    discountAmount = (subtotal * discount.value) / 100;
  } else if (discount.type === "flat") {
    discountAmount = Math.min(discount.value, subtotal);
  }
  const totalPrice = subtotal - discountAmount;
  const totalPriceWithTax =
    (totalPrice + deliveryAmount) * (getCurrencySymbol() !== "$" ? 1.18 : 1);
  const totalMrp = subtotal;
  const totalDiscount = discountAmount;

  if (loading) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading your cart...</p>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          body {
            overflow-x: hidden;
            font-family: 'Roboto', sans-serif;
          }
          .container-fluid {
            padding: 0 2rem;
            max-width: 100%;
            margin-top: 3rem;
            margin-bottom: 3rem;
          }
          .cart-card {
            width: 100%;
            max-width: 950px;
            min-height: 250px;
            margin: 0 auto 2rem;
            border-radius: 12px;
            box-shadow: 0 6px 12px rgba(0,0,0,0.1);
            background-color: #fff;
            display: flex;
            align-items: stretch;
          }
          .cart-image-container {
            background-color: #f0f0f0;
            padding: 1.5rem;
            border-radius: 12px;
            width: 220px;
            height: 180px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 1rem 0 1rem 1.5rem;
            flex-shrink: 0;
          }
          .cart-image {
            max-width: 180px;
            max-height: 140px;
            width: 100%;
            height: auto;
            object-fit: contain;
          }
          .cart-body {
            padding: 1.5rem;
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
          .cart-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 0.75rem;
          }
          .cart-title {
            font-size: 1.25rem;
            font-weight: 600;
            margin: 0;
            color: #333;
          }
          .cart-text {
            font-size: 0.9rem;
            color: #666;
            margin-bottom: 0.5rem;
            line-height: 1.5;
          }
          .cart-price {
            font-size: 1rem;
            font-weight: bold;
            color: #000;
          }
          .cart-old-price {
            font-size: 0.9rem;
            color: #888;
            text-decoration: line-through;
          }
          .cart-discount {
            font-size: 0.9rem;
            color: #28a745;
          }
          .delete-btn {
            padding: 0;
            background: none;
            border: none;
            cursor: pointer;
            flex-shrink: 0;
          }
          .delete-btn img {
            width: 20px;
            height: 20px;
            transition: opacity 0.2s;
          }
          .delete-btn img:hover {
            opacity: 0.7;
          }
          .cart-footer {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-top: 0.75rem;
          }
          .quantity-btn {
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px solid #ddd;
            border-radius: 6px;
            background-color: #f9f9f9;
            cursor: pointer;
            font-size: 1rem;
            transition: background-color 0.2s;
          }
          .quantity-btn:hover {
            background-color: #e0e0e0;
          }
          .quantity-input {
            width: 50px;
            text-align: center;
            border: 1px solid #ddd;
            border-radius: 6px;
            font-size: 0.9rem;
            background-color: #fff;
          }
          .price-details-card {
            max-width: 520px;
            margin: 0 auto 2rem;
            padding: 2rem;
            border-radius: 12px;
            box-shadow: 0 6px 12px rgba(0,0,0,0.1);
            background-color: #fff;
          }
          .proceed-btn {
            background-color: #02BE0F;
            color: white;
            padding: 0.75rem;
            border-radius: 8px;
            font-weight: 600;
            width: 100%;
            max-width: 250px;
            margin: 1.5rem auto 0;
            display: block;
            transition: background-color 0.3s, transform 0.2s;
            touch-action: manipulation;
          }
          .proceed-btn:hover, .proceed-btn:focus {
            background-color: #029b0c;
            transform: translateY(-2px);
          }
          .proceed-btn:disabled {
            background-color: #ccc;
            cursor: not-allowed;
            transform: none;
          }
          .coupon-container, .referral-container {
            display: flex;
            gap: 0.5rem;
            margin-bottom: 1rem;
          }
          .coupon-input, .referral-input {
            flex-grow: 1;
          }
          .coupon-error, .referral-error {
            color: #dc3545;
            font-size: 0.85rem;
            margin-top: 0.25rem;
          }
          .coupon-details, .referral-details {
            background: rgba(240, 240, 240, 0.5);
            border-radius: 8px;
            padding: 1rem;
            margin-bottom: 1rem;
          }
          .coupon-details h6, .referral-details h6 {
            font-size: 1rem;
            font-weight: 600;
            color: #333;
            margin-bottom: 0.5rem;
          }
          .coupon-details p, .referral-details p {
            font-size: 0.85rem;
            color: #444;
            margin-bottom: 0.25rem;
          }
          .coupon-details .btn-remove, .referral-details .btn-remove {
            background-color: #dc3545;
            color: white;
            border: none;
            padding: 0.5rem 1rem;
            border-radius: 6px;
            font-size: 0.85rem;
            cursor: pointer;
            transition: background-color 0.2s;
          }
          .coupon-details .btn-remove:hover, .referral-details .btn-remove:hover {
            background-color: #c82333;
          }
          .section-heading {
            font-size: 1.75rem;
            font-weight: 700;
            color: #333;
            margin-bottom: 2rem;
            margin-left: 130px;
          }
          .modal-backdrop {
            background: rgba(0, 0, 0, 0.6);
            backdrop-filter: blur(5px);
          }
          .modal-container {
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            padding: 1rem;
          }
          .modal-content {
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(10px);
            border-radius: 16px;
            max-width: 500px;
            width: 100%;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
            overflow: hidden;
            animation: modalFadeIn 0.3s ease-out;
            border: 1px solid rgba(255, 255, 255, 0.2);
          }
          @keyframes modalFadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .modal-header {
            padding: 1.5rem 2rem;
            border-bottom: 1px solid rgba(0, 0, 0, 0.1);
            background: transparent;
          }
          .modal-title {
            font-size: 1.5rem;
            font-weight: 700;
            color: #222;
          }
          .btn-close {
            background: none;
            border: none;
            font-size: 1.2rem;
            color: #666;
            cursor: pointer;
            transition: color 0.2s;
          }
          .btn-close:hover {
            color: #333;
          }
          .modal-body {
            padding: 2rem;
          }
          .modal-footer {
            padding: 1.5rem 2rem;
            border-top: 1px solid rgba(0, 0, 0, 0.1);
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
          }
          .payment-form {
            background: transparent;
            border-radius: 12px;
          }
          .form-label {
            font-size: 0.95rem;
            font-weight: 500;
            color: #333;
            margin-bottom: 0.5rem;
            display: block;
          }
          .form-control, .form-select {
            background: rgba(255, 255, 255, 0.8);
            border: 1px solid rgba(0, 0, 0, 0.1);
            border-radius: 8px;
            padding: 0.75rem;
            font-size: 0.95rem;
            color: #333;
            transition: border-color 0.3s, box-shadow 0.3s;
          }
          .form-control:focus, .form-select:focus {
            border-color: #02BE0F;
            box-shadow: 0 0 0 3px rgba(2, 190, 15, 0.1);
            outline: none;
          }
          .form-control::placeholder {
            color: #999;
          }
          .payment-details {
            margin-bottom: 1.5rem;
            padding: 1rem;
            background: rgba(240, 240, 240, 0.5);
            border-radius: 8px;
          }
          .payment-details p {
            margin: 0.5rem 0;
            font-size: 0.95rem;
            color: #444;
          }
          .payment-details strong {
            color: #222;
          }
          .payment-option-btn {
            padding: 0.75rem 1.5rem;
            border-radius: 8px;
            font-size: 0.95rem;
            font-weight: 600;
            transition: background-color 0.3s, transform 0.2s;
            cursor: pointer;
            touch-action: manipulation;
          }
          .btn-secondary {
            background-color: #6c757d;
            color: white;
            border: none;
          }
          .btn-secondary:hover {
            background-color: #5a6268;
            transform: translateY(-2px);
          }
          .btn-success {
            background-color: #02BE0F;
            color: white;
            border: none;
          }
          .btn-success:hover {
            background-color: #029b0c;
            transform: translateY(-2px);
          }
          .total-amount {
            font-size: 1.1rem;
            font-weight: 600;
            color: #222;
            margin-bottom: 1.5rem;
            text-align: center;
          }
          @media (min-width: 577px) and (max-width: 768px) {
            .container-fluid {
              padding: 0 1.5rem;
              margin-top: 2.5rem;
              margin-bottom: 2.5rem;
            }
            .cart-card {
              max-width: 100%;
              min-height: 220px;
              margin-bottom: 1.75rem;
            }
            .cart-image-container {
              width: 180px;
              height: 150px;
              margin: 0.75rem 0 0.75rem 1rem;
            }
            .cart-image {
              max-width: 140px;
              max-height: 110px;
            }
            .cart-body {
              padding: 1rem;
            }
            .cart-title {
              font-size: 1.125rem;
            }
            .cart-text {
              font-size: 0.85rem;
            }
            .cart-price {
              font-size: 0.95rem;
            }
            .cart-old-price, .cart-discount {
              font-size: 0.85rem;
            }
            .cart-footer {
              gap: 0.75rem;
            }
            .quantity-btn {
              width: 28px;
              height: 28px;
              font-size: 0.95rem;
            }
            .quantity-input {
              width: 45px;
              font-size: 0.85rem;
            }
            .price-details-card {
              max-width: 100%;
              padding: 1.5rem;
              margin-bottom: 1.75rem;
            }
            .proceed-btn {
              max-width: 200px;
              padding: 0.625rem;
              font-size: 0.95rem;
            }
            .modal-content {
              max-width: 450px;
            }
            .modal-body {
              padding: 1.5rem;
            }
            .modal-header, .modal-footer {
              padding: 1.25rem 1.5rem;
            }
            .section-heading {
              font-size: 1.5rem;
              margin-left: 12px;
            }
          }
          @media (min-width: 413px) and (max-width: 576px) {
            .container-fluid {
              padding: 0 1rem;
              margin-top: 2rem;
              margin-bottom: 2rem;
            }
            .cart-card {
              max-width: 100%;
              min-height: auto;
              flex-direction: column;
              padding: 1rem;
              margin-bottom: 1.5rem;
            }
            .cart-image-container {
              width: 140px;
              height: 120px;
              margin: 0 auto 0.75rem;
            }
            .cart-image {
              max-width: 110px;
              max-height: 90px;
            }
            .cart-body {
              text-align: center;
              padding: 0.75rem;
            }
            .cart-header {
              flex-direction: column;
              align-items: center;
              gap: 0.5rem;
            }
            .cart-title {
              font-size: 1rem;
            }
            .cart-text {
              font-size: 0.8rem;
              margin-bottom: 0.4rem;
            }
            .cart-price {
              font-size: 0.9rem;
            }
            .cart-old-price, .cart-discount {
              font-size: 0.8rem;
            }
            .delete-btn img {
              width: 18px;
              height: 18px;
            }
            .cart-footer {
              justify-content: center;
              gap: 0.5rem;
            }
            .quantity-btn {
              width: 26px;
              height: 26px;
              font-size: 0.9rem;
            }
            .quantity-input {
              width: 40px;
              font-size: 0.8rem;
            }
            .price-details-card {
              max-width: 100%;
              padding: 1.25rem;
              margin-bottom: 1.5rem;
            }
            .proceed-btn {
              max-width: 100%;
              padding: 0.5rem;
              font-size: 0.9rem;
            }
            .modal-content {
              max-width: 90vw;
            }
            .modal-body {
              padding: 1.25rem;
            }
            .modal-header, .modal-footer {
              padding: 1rem 1.25rem;
            }
            .payment-option-btn {
              font-size: 0.9rem;
              padding: 0.5rem 1rem;
            }
            .form-control, .form-select {
              font-size: 0.85rem;
              padding: 0.6rem;
            }
            .section-heading {
              font-size: 1.25rem;
              margin-left: 10px;
            }
          }
          @media (max-width: 412px) {
            .container-fluid {
              padding: 0 0.75rem;
              margin-top: 1.5rem;
              margin-bottom: 1.5rem;
            }
            .cart-card {
              padding: 0.75rem;
              margin-bottom: 1.25rem;
            }
            .cart-image-container {
              width: 120px;
              height: 100px;
              margin-bottom: 0.5rem;
            }
            .cart-image {
              max-width: 90px;
              max-height: 70px;
            }
            .cart-body {
              padding: 0.5rem;
            }
            .cart-title {
              font-size: 0.9rem;
            }
            .cart-text {
              font-size: 0.75rem;
            }
            .cart-price {
              font-size: 0.85rem;
            }
            .cart-old-price, .cart-discount {
              font-size: 0.75rem;
            }
            .cart-footer {
              flex-direction: column;
              align-items: center;
              gap: 0.5rem;
            }
            .quantity-btn {
              width: 24px;
              height: 24px;
              font-size: 0.85rem;
            }
            .quantity-input {
              width: 38px;
              font-size: 0.75rem;
            }
            .price-details-card {
              padding: 1rem;
              margin-bottom: 1.25rem;
            }
            .proceed-btn {
              padding: 0.4rem;
              font-size: 0.85rem;
            }
            .modal-content {
              max-width: 95vw;
            }
            .modal-body {
              padding: 1rem;
            }
            .modal-header, .modal-footer {
              padding: 0.75rem 1rem;
            }
            .payment-option-btn {
              font-size: 0.85rem;
              padding: 0.4rem 0.8rem;
            }
            .form-control, .form-select {
              font-size: 0.8rem;
              padding: 0.5rem;
            }
            .section-heading {
              font-size: 1.125rem;
              margin-left: 8px;
            }
          }
        `}
      </style>

      <div className="container-fluid">
        <h2 className="section-heading">Your Shopping Cart</h2>
        <div className="row">
          <div className="col-md-8 mx-auto">
            {cartItems?.length === 0 ? (
              <p className="text-center">Your cart is empty.</p>
            ) : (
              cartItems?.map((item) => (
                <div className="card cart-card" key={item.cartItemId}>
                  <div className="row g-0">
                    <div className="col-md-4 text-center">
                      <div className="cart-image-container">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="cart-image"
                          loading="lazy"
                        />
                      </div>
                    </div>
                    <div className="col-md-8">
                      <div className="cart-body">
                        <div className="cart-header">
                          <h5 className="cart-title">
                            {item?.productDetails?.productName}
                          </h5>
                          <button
                            className="delete-btn"
                            onClick={() => handleDelete(item)}
                            disabled={isUpdatingCart}
                          >
                            <img src="/delete.png" alt="Delete" />
                          </button>
                        </div>
                        <p className="cart-text">
                          {item?.productDetails?.briefDescription}
                        </p>
                        <p className="cart-text mb-2">
                          <strong className="cart-price">
                            {getCurrencySymbol()}
                            {convertPrice(item?.price?.toFixed(2))}
                          </strong>
                        </p>
                        {item?.productDetails?.productType === "physical" && (
                          <div className="cart-footer">
                            <label className="me-2">Qty:</label>
                            <button
                              className="quantity-btn"
                              onClick={() => handleQuantityChange(item, -1)}
                              disabled={isUpdatingCart}
                            >
                              -
                            </button>
                            <input
                              type="number"
                              className="quantity-input"
                              value={item.quantity}
                              readOnly
                            />
                            <button
                              className="quantity-btn"
                              onClick={() => handleQuantityChange(item, 1)}
                              disabled={isUpdatingCart}
                            >
                              +
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="col-md-4">
            <div className="card price-details-card">
              <h5>Price Details</h5>
              <hr />
              <div className="d-flex justify-content-between">
                <span>Total MRP:</span>
                <span>
                  {getCurrencySymbol()}
                  {convertPrice(totalMrp.toFixed(2))}
                </span>
              </div>
              <div className="d-flex justify-content-between">
                <span>Discount on MRP:</span>
                <span>
                  {getCurrencySymbol()}
                  {convertPrice(totalDiscount.toFixed(2))}
                </span>
              </div>
              {hasPhysicalProducts && (
                <div className="d-flex justify-content-between">
                  <span>Delivery Charges:</span>
                  <span>
                    {getCurrencySymbol()}
                    {convertPrice(deliveryAmount.toFixed(2))}
                  </span>
                </div>
              )}
              {getCurrencySymbol() !== "$" && (
                <div className="d-flex justify-content-between">
                  <span>GST (18%):</span>
                  <span>
                    ₹
                    {(totalPriceWithTax - totalPrice - deliveryAmount).toFixed(
                      2,
                    )}
                  </span>
                </div>
              )}

              <div className="coupon-container mt-3">
                <input
                  type="text"
                  className="form-control coupon-input"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  placeholder="Enter Coupon Code"
                  disabled={couponDetails || isApplyingCoupon}
                />
                <button
                  className="btn btn-success"
                  onClick={handleApplyCoupon}
                  disabled={!couponCode || couponDetails || isApplyingCoupon}
                >
                  {isApplyingCoupon ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Applying...
                    </>
                  ) : (
                    "Apply"
                  )}
                </button>
              </div>
              {couponError && <div className="coupon-error">{couponError}</div>}
              {couponDetails && (
                <div className="coupon-details">
                  <h6>{couponDetails.title}</h6>
                  <p>{couponDetails.description}</p>
                  <p>
                    <strong>Code:</strong> {couponDetails.couponCode}
                  </p>
                  <p>
                    <strong>Discount:</strong>{" "}
                    {couponDetails.discountType === "percentage"
                      ? `${couponDetails.discountValue}% off`
                      : `₹${couponDetails.discountValue} off`}
                  </p>
                  <button
                    className="btn btn-remove"
                    onClick={handleRemoveCoupon}
                    disabled={isApplyingCoupon}
                  >
                    Remove Coupon
                  </button>
                </div>
              )}

              <div className="referral-container mt-3">
                <input
                  type="text"
                  className="form-control referral-input"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value)}
                  placeholder="Enter Referral Code"
                  disabled={referralVerified || isVerifyingReferral}
                />
                <button
                  className="btn btn-success"
                  onClick={handleVerifyReferral}
                  disabled={
                    !referralCode || referralVerified || isVerifyingReferral
                  }
                >
                  {isVerifyingReferral ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Verifying...
                    </>
                  ) : (
                    "Verify"
                  )}
                </button>
              </div>
              {referralError && (
                <div className="referral-error">{referralError}</div>
              )}
              {referralVerified && (
                <div className="referral-details">
                  <h6>Referral Code Applied</h6>
                  <p>
                    <strong>Code:</strong> {referralCode}
                  </p>
                  <button
                    className="btn btn-remove"
                    onClick={handleRemoveReferral}
                    disabled={isVerifyingReferral}
                  >
                    Remove Referral
                  </button>
                </div>
              )}

              <hr />
              <div className="d-flex justify-content-between mb-3">
                <h5>Total Amount :</h5>
                <h5>
                  {getCurrencySymbol()}
                  {convertPrice(totalPriceWithTax.toFixed(2))}
                  {/* ₹{totalPriceWithTax.toFixed(2)} */}
                </h5>
              </div>

              <button
                className="btn proceed-btn"
                onClick={handleMakeOrder}
                disabled={cartItems?.length === 0 || isPlacingOrder}
              >
                {isPlacingOrder ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Processing...
                  </>
                ) : (
                  "Proceed to Payment"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default Cart;
